import { useState, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useModels, type LlmModel } from '@/hooks/useModels';
import { Search, Star, Loader2, Eye, Brain, Image, Code, Check, Wrench, ChevronDown, ChevronUp, Sparkles, Info, Globe, AudioLines } from 'lucide-react';
import type { Json } from '@/integrations/supabase/types';

interface ModelSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onSelect: (modelId: string) => void;
  requiredModality?: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  deepseek: 'DeepSeek',
};

const PRICE_COLORS: Record<string, string> = {
  '$': 'bg-emerald-100 text-emerald-700',
  '$$': 'bg-amber-100 text-amber-700',
  '$$$': 'bg-red-100 text-red-700',
};

type CapabilityKey = 'vision' | 'audio' | 'thinking' | 'image_gen' | 'code' | 'tools' | 'web_search';

const CAPABILITY_FILTERS: { key: CapabilityKey; label: string; icon: typeof Eye }[] = [
  { key: 'thinking', label: 'Raciocínio', icon: Brain },
  { key: 'image_gen', label: 'Imagem', icon: Image },
  { key: 'vision', label: 'Visão', icon: Eye },
  { key: 'audio', label: 'Áudio', icon: AudioLines },
  { key: 'web_search', label: 'Web', icon: Globe },
  { key: 'code', label: 'Código', icon: Code },
  { key: 'tools', label: 'Tools', icon: Wrench },
];

function getCapabilities(cap: Json): Record<string, boolean> {
  if (typeof cap === 'object' && cap !== null && !Array.isArray(cap)) {
    return cap as Record<string, boolean>;
  }
  return {};
}

export default function ModelSelectorModal({ open, onOpenChange, value, onSelect, requiredModality }: ModelSelectorModalProps) {
  const isMobile = useIsMobile();
  const { data: models, isLoading } = useModels();
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<string | null>(null);
  const [capFilters, setCapFilters] = useState<Set<CapabilityKey>>(new Set());
  const [showRecommended, setShowRecommended] = useState(true);
  const [expandedModelId, setExpandedModelId] = useState<string | null>(null);

  const toggleCap = (key: CapabilityKey) => {
    setCapFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size > 0) {
        setShowRecommended(false);
        setProviderFilter(null);
      }
      return next;
    });
  };

  const providers = useMemo(() => {
    if (!models) return [];
    return [...new Set(models.map(m => m.provider))];
  }, [models]);

  const hasRecommended = useMemo(() => {
    return models?.some(m => m.is_recommended) ?? false;
  }, [models]);

  const filtered = useMemo(() => {
    if (!models) return [];
    let list = models;
    if (requiredModality) {
      list = list.filter(m => (m.input_modalities ?? []).includes(requiredModality));
    }
    if (showRecommended) {
      list = list.filter(m => m.is_recommended);
    }
    if (providerFilter) list = list.filter(m => m.provider === providerFilter);
    if (capFilters.size > 0) {
      list = list.filter(m => {
        const caps = getCapabilities(m.capabilities);
        return [...capFilters].every(key => caps[key] === true);
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.display_name.toLowerCase().includes(q) ||
        m.provider.toLowerCase().includes(q)
      );
    }
    return list;
  }, [models, providerFilter, capFilters, search, requiredModality, showRecommended]);

  const handleSelect = (id: string) => {
    onSelect(id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[600px] p-0 gap-0 overflow-hidden [&>*]:min-w-0">
        <DialogTitle className="sr-only">{requiredModality ? `Selecionar modelo com suporte a ${requiredModality}` : 'Selecionar modelo'}</DialogTitle>

        {/* Search + capability filters */}
        <div className="p-3 border-b border-color space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-tertiary" size={16} />
            <Input
              placeholder="Buscar modelos..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-1.5 flex-wrap">
              {CAPABILITY_FILTERS.map(({ key, label, icon: Icon }) => {
                const active = capFilters.has(key);
                return (
                  <Tooltip key={key}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => toggleCap(key)}
                        className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-xs font-medium transition-all border ${
                          active
                            ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                            : 'bg-secondary border-color text-tertiary hover:text-secondary hover:border-color'
                        }`}
                      >
                        <Icon size={13} />
                        <span>{label}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {active ? `Remover filtro: ${label}` : `Filtrar por: ${label}`}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        </div>

        <div className={`flex min-w-0 w-full overflow-hidden ${isMobile ? 'flex-col min-h-[70vh] max-h-[80vh]' : 'min-h-[320px] max-h-[420px]'}`}>
          <div className={
            isMobile
              ? 'w-full shrink-0 border-b border-color flex gap-1 px-2 py-2 overflow-x-auto'
              : 'w-28 shrink-0 border-r border-color flex flex-col gap-0.5 py-2 px-1.5'
          }>
            {hasRecommended && (
              <button
                onClick={() => { setShowRecommended(!showRecommended); setProviderFilter(null); }}
                className={
                  isMobile
                    ? `shrink-0 h-7 px-3 rounded-full text-xs font-medium transition-colors ${showRecommended ? 'bg-amber-500/15 text-amber-600' : 'bg-secondary text-secondary'}`
                    : `w-full h-9 rounded-md flex items-center px-2 text-xs font-medium transition-colors mb-1 ${showRecommended ? 'bg-amber-500/15 text-amber-600' : 'text-secondary hover:bg-hover'}`
                }
              >
                Destaques
              </button>
            )}

            {!isMobile && <div className="h-px bg-border my-1" />}
            {providers.map(p => (
              <button
                key={p}
                onClick={() => { setProviderFilter(providerFilter === p ? null : p); setShowRecommended(false); }}
                className={
                  isMobile
                    ? `shrink-0 h-7 px-3 rounded-full text-xs font-medium transition-colors ${providerFilter === p ? 'bg-active text-primary' : 'bg-secondary text-secondary'}`
                    : `w-full h-8 rounded-md flex items-center px-2 text-xs font-medium transition-colors ${providerFilter === p ? 'bg-active text-primary' : 'text-secondary hover:bg-hover'}`
                }
              >
                {PROVIDER_LABELS[p] ?? p}
              </button>
            ))}
            {!isMobile && <div className="h-px bg-border my-1" />}
            <button
              onClick={() => { setProviderFilter(null); setShowRecommended(false); }}
              className={
                isMobile
                  ? `shrink-0 h-7 px-3 rounded-full text-xs font-medium transition-colors ${providerFilter === null && !showRecommended ? 'bg-active text-primary' : 'bg-secondary text-secondary'}`
                  : `w-full h-8 rounded-md flex items-center gap-2 px-2 text-xs font-medium transition-colors ${providerFilter === null && !showRecommended ? 'bg-active text-primary' : 'text-secondary hover:bg-hover'}`
              }
            >
              Todos
            </button>
          </div>

          {/* Model list */}
          <div className="flex-1 min-w-0 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-full py-16">
                <Loader2 className="animate-spin text-tertiary" size={24} />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-secondary text-sm text-center py-16">Nenhum modelo encontrado</p>
            ) : (
              <div className="py-1">
                {filtered.map(model => {
                  const caps = getCapabilities(model.capabilities);
                  const isSelected = model.id === value;
                  const isExpanded = expandedModelId === model.id;
                  return (
                    <div key={model.id}>
                      <button
                        onClick={() => handleSelect(model.id)}
                        className={`w-full max-w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors cursor-pointer overflow-hidden ${
                          isSelected ? 'bg-hover' : 'hover:bg-hover/50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            {!isMobile && (
                              <span className="text-[10px] font-bold uppercase text-tertiary tracking-wide shrink-0">
                                {model.provider}
                              </span>
                            )}
                            <span className="text-sm font-semibold text-primary truncate min-w-0">
                              {model.display_name}
                            </span>
                            {model.price_tier && !showRecommended && (
                              <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-4 shrink-0 ${PRICE_COLORS[model.price_tier] ?? ''}`}>
                                {model.price_tier}
                              </Badge>
                            )}
                          </div>
                          {model.is_recommended && model.recommendation_note && (
                            <p className="text-xs text-secondary mt-0.5 line-clamp-1">{model.recommendation_note}</p>
                          )}
                          {(!model.is_recommended || !model.recommendation_note) && model.description && (
                            <p className="text-xs text-secondary mt-0.5 line-clamp-1">{model.description}</p>
                          )}
                          {isMobile && (
                            <div className="flex items-center gap-2 mt-1.5 text-tertiary">
                              {caps.vision && <Eye size={13} />}
                              {caps.audio && <AudioLines size={13} />}
                              {caps.thinking && <Brain size={13} />}
                              {caps.code && <Code size={13} />}
                              {caps.image_gen && <Image size={13} />}
                              {caps.tools && <Wrench size={13} />}
                            </div>
                          )}
                        </div>

                        {/* Capabilities + expand + check */}
                        <TooltipProvider delayDuration={200}>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {!isMobile && <>
                              {caps.vision && (
                              <Tooltip>
                                <TooltipTrigger asChild><span><Eye size={14} className="text-tertiary" /></span></TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">Visão</TooltipContent>
                              </Tooltip>
                            )}
                            {caps.audio && (
                              <Tooltip>
                                <TooltipTrigger asChild><span><AudioLines size={14} className="text-tertiary" /></span></TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">Áudio</TooltipContent>
                              </Tooltip>
                            )}
                            {caps.thinking && (
                              <Tooltip>
                                <TooltipTrigger asChild><span><Brain size={14} className="text-tertiary" /></span></TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">Raciocínio</TooltipContent>
                              </Tooltip>
                            )}
                            {caps.code && (
                              <Tooltip>
                                <TooltipTrigger asChild><span><Code size={14} className="text-tertiary" /></span></TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">Código</TooltipContent>
                              </Tooltip>
                            )}
                            {caps.image_gen && (
                              <Tooltip>
                                <TooltipTrigger asChild><span><Image size={14} className="text-tertiary" /></span></TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">Geração de imagem</TooltipContent>
                              </Tooltip>
                            )}
                            {caps.tools && (
                              <Tooltip>
                                <TooltipTrigger asChild><span><Wrench size={14} className="text-tertiary" /></span></TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">Tools</TooltipContent>
                              </Tooltip>
                            )}
                            </>}
                            {model.description && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedModelId(isExpanded ? null : model.id);
                                }}
                                className="p-0.5 text-tertiary hover:text-secondary transition-colors"
                              >
                                {isExpanded ? <Info size={14} className="bg-inherit text-secondary-foreground" /> : <Info size={14} />}
                              </button>
                            )}
                            {isSelected && <Check size={14} className="text-brand-primary ml-1" />}
                          </div>
                        </TooltipProvider>
                      </button>
                      {/* Expanded description */}
                      {isExpanded && model.description && (
                        <div className="px-3 pb-2.5 -mt-1">
                          <p className="text-xs text-secondary bg-secondary/50 rounded-md p-2.5 leading-relaxed">
                            {model.description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
